import { yupResolver } from '@hookform/resolvers/yup'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'i11n/client'
import { useForm } from 'react-hook-form'
import {
  MessageTemplateType,
  MessageTemplateTypes,
  resolveMessageTemplateType,
} from '../constants/message-template'
import {
  DeliveryChannel,
  MessageTemplateCtx,
} from '../providers/MessageTemplate.provider'
import {
  IMessageTemplateSchema,
  buildMessageTemplateSchema,
} from '../schemas/message-template.schema'
import { useRouter } from 'next/router'

type MessageTemplateDefaultValues = Pick<
  IMessageTemplateSchema,
  'subject' | 'body' | 'buttonLabel' | 'type'
>

type FieldWithVariable = 'subject' | 'body' | 'footer'

const messageTemplateDefaultValuesByType = {
  [MessageTemplateTypes.REQUEST]: {
    subject: 'Hola',
    body: 'Para iniciar la inspección, presiona el botón que se encuentra abajo\nTienes {{inspection.rules.deadlineDays}} días para ejecutarla',
    buttonLabel: 'Iniciar inspección',
    type: MessageTemplateTypes.REQUEST,
  },
  [MessageTemplateTypes.BLOCKED]: {
    subject: 'Hola',
    body: 'Te avisamos que la siguiente inspección se bloqueó por expiración.',
    buttonLabel: 'Desbloquear inspección',
    type: MessageTemplateTypes.BLOCKED,
  },
  [MessageTemplateTypes.PENDING]: {
    subject: 'Hola',
    body: 'Te damos la bienvenida nuevamente.  Para iniciar la autoinspección, presiona el botón que se encuentra abajo. \nTienes {{inspection.rules.deadlineDays}} días para ejecutarla, en caso contrario esta será bloqueada.',
    buttonLabel: 'Continuar inspección',
    type: MessageTemplateTypes.PENDING,
  },
  [MessageTemplateTypes.FINISHED]: {
    subject: 'Hola',
    body: 'Te informamos que la inspección ha sido finalizada. Puedes descargar el certificado a continuación. \nSaludos, Equipo de Autoinspector',
    buttonLabel: 'Descargar certificado',
    type: MessageTemplateTypes.FINISHED,
  },
} satisfies Record<MessageTemplateType, MessageTemplateDefaultValues>

const getMessageTemplateDefaultValues = (
  type: MessageTemplateType,
  defaultValues?: Partial<IMessageTemplateSchema>,
) => {
  const defaultValuesByType = messageTemplateDefaultValuesByType[type]

  return {
    subject: defaultValues?.subject ?? defaultValuesByType.subject,
    body: defaultValues?.body ?? defaultValuesByType.body,
    footer: defaultValues?.footer,
    buttonLabel: defaultValues?.buttonLabel ?? defaultValuesByType.buttonLabel,
    type: defaultValues?.type
      ? resolveMessageTemplateType(defaultValues.type)
      : defaultValuesByType.type,
  }
}

export const useMessageTemplateForm = (props: {
  defaultValues?: Partial<IMessageTemplateSchema>
  channel?: DeliveryChannel
}) => {
  const t = useTranslation()
  const router = useRouter()
  const type = useMemo(
    () => resolveMessageTemplateType(router?.query?.flow),
    [router?.query?.flow],
  )

  const { logo, deadlineDays } = useContext(MessageTemplateCtx)
  const schema = useMemo(() => buildMessageTemplateSchema(t), [t])
  const defaultSubject = props.defaultValues?.subject
  const defaultBody = props.defaultValues?.body
  const defaultFooter = props.defaultValues?.footer
  const defaultButtonLabel = props.defaultValues?.buttonLabel
  const defaultType = props.defaultValues?.type
  const initVals = useMemo(
    () =>
      getMessageTemplateDefaultValues(type, {
        subject: defaultSubject,
        body: defaultBody,
        footer: defaultFooter,
        buttonLabel: defaultButtonLabel,
        type: defaultType,
      }),
    [
      defaultBody,
      defaultButtonLabel,
      defaultFooter,
      defaultSubject,
      defaultType,
      type,
    ],
  )
  const formDefaultValues = useMemo(
    () => ({
      ...initVals,
      deadlineDays,
      logo,
      channel: props.channel,
    }),
    [deadlineDays, initVals, logo, props.channel],
  )

  const form = useForm<IMessageTemplateSchema>({
    resolver: yupResolver(schema),
    defaultValues: formDefaultValues,
  })

  const { reset } = form

  useEffect(() => {
    reset(formDefaultValues)
  }, [formDefaultValues, reset])

  const {
    register,
    watch,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = form

  const customSubjectRef = useRef<HTMLInputElement | null>(null)
  const customBodyRef = useRef<HTMLTextAreaElement | null>(null)
  const customFooterRef = useRef<HTMLTextAreaElement | null>(null)
  const customButtonLabelRef = useRef<HTMLInputElement | null>(null)
  const variableFieldCursorPositionsRef = useRef<
    Record<FieldWithVariable, number | null>
  >({
    subject: null,
    body: null,
    footer: null,
  })

  const { ref: bodyRef, ...bodyRest } = register('body')
  const { ref: subjectRef, ...subjectRest } = register('subject')
  const { ref: footerRef, ...footerRest } = register('footer')
  const { ref: buttonLabelRef, ...buttonLabelRest } = register('buttonLabel')

  const fields = watch()

  const refs = useMemo(
    () => ({
      subject: customSubjectRef,
      body: customBodyRef,
      footer: customFooterRef,
    }),
    [customBodyRef, customFooterRef, customSubjectRef],
  )

  const updateVariableFieldCursorPosition = useCallback(
    (
      input: FieldWithVariable,
      refToExtractCursorPosition: HTMLInputElement | HTMLTextAreaElement,
    ) => {
      variableFieldCursorPositionsRef.current[input] =
        refToExtractCursorPosition.selectionStart
    },
    [],
  )

  const updateFieldWithVariable = useCallback(
    (
      input: FieldWithVariable,
      refToExtractCursorPosition: HTMLInputElement | HTMLTextAreaElement | null,
      variable: string,
    ) => {
      const currentValue =
        refToExtractCursorPosition?.value ?? getValues(input) ?? ''
      const cursorPosition = Math.min(
        variableFieldCursorPositionsRef.current[input] ??
          refToExtractCursorPosition?.selectionStart ??
          currentValue.length,
        currentValue.length,
      )
      const variableValue = ` {{${variable}}} `
      const nextCursorPosition = cursorPosition + variableValue.length
      const nextValue = `${currentValue.substring(
        0,
        cursorPosition,
      )}${variableValue}${currentValue.substring(
        cursorPosition,
        currentValue.length,
      )}`

      setValue(input, nextValue, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })

      variableFieldCursorPositionsRef.current[input] = nextCursorPosition

      if (!refToExtractCursorPosition) return
      refToExtractCursorPosition.focus()
      requestAnimationFrame(() => {
        refToExtractCursorPosition.setSelectionRange(
          nextCursorPosition,
          nextCursorPosition,
        )
      })
    },
    [getValues, setValue],
  )

  const onAddVariable = useCallback(
    (input: FieldWithVariable, variable: string) => {
      const refToExtractCursorPosition = refs[input].current

      updateFieldWithVariable(input, refToExtractCursorPosition, variable)
    },
    [refs, updateFieldWithVariable],
  )

  return {
    errors,
    onAddVariable,
    updateVariableFieldCursorPosition,
    fields,
    handleSubmit,
    logo,
    deadlineDays,
    bodyRest,
    subjectRest,
    footerRest,
    buttonLabelRest,
    customButtonLabelRef,
    bodyRef,
    footerRef,
    subjectRef,
    buttonLabelRef,
    register,
    customSubjectRef,
    customBodyRef,
    customFooterRef,
    form,
  }
}
